import React, { useState, useEffect, useRef } from 'react';
import {
  CreditCard,
  User,
  Phone,
  Calendar,
  MapPin,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Star,
  Clock,
  X,
  PanelLeft,
  PanelRight,
  ArrowLeftRight,
  MessageSquare,
  History,
  CalendarCheck
} from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';
import sessionManager from '../../services/SessionManager';

const UHIDSelector = ({
  currentUser,
  selectedUHID,
  selectedPatientDetails,
  onSelectUHID,
  onSelectAppointment,
  onSelectAppointmentWithoutModal,
  refreshTrigger,
  chatHistoryRefreshTrigger,
  onSelectChat,
  isOpen = true,
  position = 'left',
  onToggle,
  onPositionChange
}) => {
  const sidebarRef = useRef(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingChatHistory, setLoadingChatHistory] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [activeTab, setActiveTab] = useState('uhid'); // 'uhid', 'chat', or 'appointments'
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState(null); // Track highlighted appointment

  useEffect(() => {
    if (currentUser) {
      fetchUserPatients();
    }
  }, [currentUser]);

  // Refresh UHID list when refreshTrigger changes
  useEffect(() => {
    if (currentUser && refreshTrigger > 0) {
      console.log('🔄 Refresh triggered - reloading patient data from database...');
      fetchUserPatients();
    }
  }, [refreshTrigger]);

  // Keyboard accessibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Esc key closes sidebar
      if (e.key === 'Escape' && isOpen) {
        onToggle?.(false);
      }

      // Enter or Space toggles when sidebar button is focused
      if ((e.key === 'Enter' || e.key === ' ') && document.activeElement?.dataset?.sidebarToggle) {
        e.preventDefault();
        onToggle?.(!isOpen);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onToggle]);

  const fetchUserPatients = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Fetching REAL patient data from database...');
      console.log('👤 Logged-in User:', {
        email: currentUser.email,
        phone: currentUser.phoneNumber,
        isAdmin: currentUser.is_admin
      });

      // Build the query based on user type
      const queryParams = new URLSearchParams();

      if (!currentUser.is_admin) {
        // Regular user - fetch by phone or email
        if (currentUser.phoneNumber) {
          queryParams.append('phoneNumber', currentUser.phoneNumber);
          console.log('📞 Searching by phone:', currentUser.phoneNumber);
        } else if (currentUser.email) {
          queryParams.append('email', currentUser.email);
          console.log('📧 Searching by email:', currentUser.email);
        }
      } else {
        console.log('👨‍💼 Admin user - fetching all patients');
      }

      const queryString = queryParams.toString();
      const apiPath = `/backend/api/patients/search${queryString ? `?${queryString}` : ''}`;
      console.log('🌐 API Request:', `${process.env.REACT_APP_API_URL}${apiPath}`);

      // Ensure session is valid and token available before hitting protected endpoint
      const validation = await sessionManager.validateSession();
      if (!validation.valid) {
        console.warn('Session not valid before patients fetch:', validation.reason);
        setError('Your session has expired. Please log in again.');
        setPatients([]);
        return;
      }

      let response = await apiFetch(apiPath, { method: 'GET' });

      // If unauthorized, attempt one re-validation and retry once
      if (response.status === 401) {
        console.warn('Patients fetch returned 401; revalidating session and retrying once...');
        const recheck = await sessionManager.validateSession();
        if (recheck.valid) {
          response = await apiFetch(apiPath, { method: 'GET' });
        }
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch patients: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ API Response:', data);

      if (data.success && data.patients && data.patients.length > 0) {
        console.log(`📋 Found ${data.patients.length} patient record(s) from DATABASE:`);
        data.patients.forEach((patient, index) => {
          console.log(`  ${index + 1}. UHID: ${patient.uhid} - ${patient.fullName}`);
        });
        setPatients(data.patients);
      } else {
        console.log('❌ No patient records found in database for this user');
        setPatients([]);
      }
    } catch (err) {
      console.error('❌ Error fetching patients:', err);
      setError('Failed to load patient records. Please try again.');
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all chat history for the user
  useEffect(() => {
    if (currentUser && activeTab === 'chat' && selectedUHID) {
      fetchAllChatHistory();
    }
  }, [currentUser, activeTab, selectedUHID]);  // Re-fetch when selectedUHID changes

  // Refresh chat history when chatHistoryRefreshTrigger changes
  useEffect(() => {
    if (currentUser && chatHistoryRefreshTrigger > 0 && selectedUHID) {
      console.log('🔄 Chat history refresh triggered - reloading from database...');
      fetchAllChatHistory();
    }
  }, [chatHistoryRefreshTrigger]);

  const fetchAllChatHistory = async () => {
    try {
      setLoadingChatHistory(true);
      const response = await apiFetch(
        '/backend/api/chat/history/all',
        {
          method: 'GET'
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.history) {
          // Filter chat history by selected UHID if one is selected
          const filteredHistory = selectedUHID
            ? data.history.filter(chat => chat.uhid === selectedUHID)
            : data.history;
          setChatHistory(filteredHistory);
          console.log(`✅ Loaded ${filteredHistory.length} chat(s) ${selectedUHID ? `for UHID ${selectedUHID}` : 'total'}`);
        }
      }
    } catch (err) {
      console.error('Error fetching chat history:', err);
    } finally {
      setLoadingChatHistory(false);
    }
  };

  // Fetch appointments for selected UHID
  useEffect(() => {
    console.log('📅 Appointments useEffect triggered:', {
      selectedUHID,
      activeTab,
      shouldFetch: selectedUHID && activeTab === 'appointments'
    });

    if (selectedUHID && activeTab === 'appointments') {
      console.log('✅ Conditions met - calling fetchAppointments()');
      fetchAppointments();
    } else {
      console.log('❌ Not fetching appointments:', {
        hasUHID: !!selectedUHID,
        isAppointmentsTab: activeTab === 'appointments'
      });
    }
  }, [selectedUHID, activeTab]);

  const fetchAppointments = async () => {
    console.log('🔍 fetchAppointments called with UHID:', selectedUHID);

    if (!selectedUHID) {
      console.log('⚠️ No UHID selected, returning early');
      return;
    }

    try {
      setLoadingAppointments(true);
      const apiPath = `/backend/api/appointments/patient/${selectedUHID}`;
      console.log('🌐 Fetching appointments from:', `${process.env.REACT_APP_API_URL}${apiPath}`);

      const response = await apiFetch(apiPath, {
        method: 'GET'
      });

      console.log('📡 Response status:', response.status, response.ok ? '✅' : '❌');

      if (response.ok) {
        const data = await response.json();
        console.log('📦 Appointments API response:', data);

        if (data.success && data.appointments) {
          console.log(`✅ Received ${data.appointments.length} appointment(s) from backend`);

          // Map backend field names to frontend expected names
          const mappedAppointments = data.appointments.map(apt => ({
            ...apt,
            doctorName: apt.doctor || apt.doctorName,
            appointmentDate: apt.date || apt.appointmentDate,
            timeSlot: apt.slot || apt.timeSlot
          }));

          console.log('✅ Mapped appointments:', mappedAppointments);
          setAppointments(mappedAppointments);
        } else {
          console.log('⚠️ No appointments in response, setting empty array');
          setAppointments([]);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ API request failed:', response.status, errorText);
        setAppointments([]);
      }
    } catch (err) {
      console.error('❌ Error fetching appointments:', err);
      setAppointments([]);
    } finally {
      setLoadingAppointments(false);
      console.log('🏁 fetchAppointments completed');
    }
  };

  const handleSelectUHID = (uhid, patientDetails) => {
    if (selectedUHID === uhid) {
      onSelectUHID(null, null);
      setExpandedPatient(null);
      setHighlightedAppointmentId(null);
    } else {
      onSelectUHID(uhid, patientDetails);
      setExpandedPatient(uhid);
    }
  };

  const toggleExpand = (uhid) => {
    setExpandedPatient(expandedPatient === uhid ? null : uhid);
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 p-6">
          <div className="h-6 bg-white/20 rounded w-1/2 mb-2 animate-pulse"></div>
          <div className="h-4 bg-white/10 rounded w-2/3 animate-pulse"></div>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gradient-to-r from-gray-100 to-gray-50 rounded-xl p-5 animate-pulse">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {/* Minimized Sidebar View - Shows when closed */}
      {!isOpen && (
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-2xl shadow-xl h-full flex flex-col items-center py-4 px-2 space-y-4">
          {/* Expand Button */}
          <button
            onClick={() => onToggle?.(true)}
            data-sidebar-toggle="true"
            className="p-3 bg-white/20 hover:bg-white/30 rounded-xl transition-all duration-300 hover:scale-110 group"
            aria-label="Open sidebar"
            title="Open Patient Records (Press Enter/Space)"
          >
            {position === 'left' ? (
              <PanelRight className="w-6 h-6 text-white" />
            ) : (
              <PanelLeft className="w-6 h-6 text-white" />
            )}
          </button>

          {/* Vertical divider */}
          <div className="w-8 h-px bg-white/30"></div>

          {/* Patient Count */}
          <div className="flex flex-col items-center space-y-1">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-bold text-sm">{patients.length}</span>
            <span className="text-white/70 text-xs writing-mode-vertical">Patients</span>
          </div>

          {/* Selected UHID Indicator */}
          {selectedUHID && (
            <>
              <div className="w-8 h-px bg-white/30"></div>
              <div className="flex flex-col items-center space-y-2">
                <div className="w-10 h-10 bg-green-500/30 backdrop-blur-sm rounded-xl flex items-center justify-center animate-pulse">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-white/90 text-xs font-mono transform rotate-90 whitespace-nowrap origin-center">
                  {selectedUHID.substring(0, 8)}
                </span>
              </div>
            </>
          )}

          {/* Position Switch */}
          <div className="flex-1"></div>
          <button
            onClick={() => onPositionChange?.(position === 'left' ? 'right' : 'left')}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200"
            aria-label="Switch sidebar position"
            title={`Move to ${position === 'left' ? 'right' : 'left'}`}
          >
            <ArrowLeftRight className="w-5 h-5 text-white" />
          </button>
        </div>
      )}

      {/* Main Sidebar Content - Full Width View */}
      <div
        ref={sidebarRef}
        className={`h-full bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl overflow-hidden border border-gray-100 transition-all duration-300 flex flex-col ${
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 absolute pointer-events-none'
        }`}
      >
        {/* Professional Header with Gradient */}
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-12 -mb-12"></div>

          <div className="relative z-10 p-6 pb-0">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3 flex-1">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white tracking-tight">
                    {activeTab === 'uhid' ? 'Patient Records' : activeTab === 'chat' ? 'Chat History' : 'Appointments'}
                  </h2>
                  <p className="text-blue-100 text-sm">
                    {activeTab === 'uhid'
                      ? (currentUser?.is_admin
                          ? `${patients.length} Total Patients`
                          : `${patients.length} Registered Profile${patients.length !== 1 ? 's' : ''}`)
                      : activeTab === 'chat'
                        ? selectedUHID
                          ? `${chatHistory.length} Conversation${chatHistory.length !== 1 ? 's' : ''} for ${selectedUHID}${selectedPatientDetails?.fullName ? ` - ${selectedPatientDetails.fullName}` : ''}`
                          : 'Select a UHID to view chat history'
                        : selectedUHID
                          ? `${appointments.length} Appointment${appointments.length !== 1 ? 's' : ''} for ${selectedUHID}${selectedPatientDetails?.fullName ? ` - ${selectedPatientDetails.fullName}` : ''}`
                          : 'Select a UHID to view appointments'
                    }
                  </p>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center space-x-2">
                {/* Position Switch Button */}
                <button
                  onClick={() => onPositionChange?.(position === 'left' ? 'right' : 'left')}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 group"
                  aria-label="Switch sidebar position"
                  title={`Move to ${position === 'left' ? 'right' : 'left'}`}
                >
                  <ArrowLeftRight className="w-5 h-5 text-white" />
                </button>

                {/* Close Button */}
                <button
                  onClick={() => onToggle?.(false)}
                  data-sidebar-toggle="true"
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-200 group"
                  aria-label="Close sidebar"
                  title="Close sidebar (Press Esc)"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Tab Buttons */}
            <div className="flex space-x-1 pb-4">
              <button
                onClick={() => setActiveTab('uhid')}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-t-lg text-sm font-medium transition-all ${
                  activeTab === 'uhid'
                    ? 'bg-white text-indigo-600 shadow-md'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <User className="w-4 h-4" />
                <span>Patients</span>
                <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
                  {patients.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-t-lg text-sm font-medium transition-all ${
                  activeTab === 'chat'
                    ? 'bg-white text-indigo-600 shadow-md'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                <span>Chats</span>
                <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
                  {chatHistory.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('appointments')}
                className={`flex items-center space-x-1.5 px-2.5 py-2 rounded-t-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'appointments'
                    ? 'bg-white text-indigo-600 shadow-md'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <CalendarCheck className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Appointments</span>
                <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
                  {appointments.length}
                </span>
              </button>
            </div>
          </div>
        </div>

      {/* Content Area - UHID List, Chat History, or Appointments */}
      <div className="flex-1 p-4 overflow-y-auto custom-scrollbar">

        {/* UHID Tab Content */}
        {activeTab === 'uhid' && (
          <>
            {error && (
              <div className="bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 rounded-xl p-4 mb-4 shadow-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-red-200 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-bold">!</span>
                  </div>
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              </div>
            )}

            {patients.length === 0 && !loading ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
              <CreditCard className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No patient records found
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Please register as a patient first to view records.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {patients.map((patient, index) => (
              <div
                key={patient.uhid}
                className={`group relative overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-[1.02] ${
                  selectedUHID === patient.uhid
                    ? 'bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-2 border-blue-400 shadow-lg'
                    : 'bg-white border-2 border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
                style={{
                  animationDelay: `${index * 50}ms`
                }}
              >
                {/* Selection Indicator */}
                {selectedUHID === patient.uhid && (
                  <div className="absolute top-0 right-0 w-0 h-0 border-t-[40px] border-r-[40px] border-t-blue-600 border-r-transparent">
                    <CheckCircle className="absolute -top-9 -right-9 w-5 h-5 text-white" />
                  </div>
                )}

                <div
                  className="p-3 cursor-pointer"
                  onClick={() => handleSelectUHID(patient.uhid, patient)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Avatar Circle */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md transition-all ${
                        selectedUHID === patient.uhid
                          ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                          : 'bg-gradient-to-br from-gray-100 to-gray-200'
                      }`}>
                        <User className={`w-5 h-5 ${
                          selectedUHID === patient.uhid ? 'text-white' : 'text-gray-600'
                        }`} />
                      </div>

                      {/* Patient Info */}
                      <div className="flex-1 min-w-0">
                        {/* Name and UHID */}
                        <div className="mb-2">
                          <h3 className="text-base font-bold text-gray-800 mb-1 truncate">
                            {patient.fullName}
                          </h3>
                          <div className="flex items-center space-x-2">
                            <div className={`flex items-center space-x-2 px-2 py-1 rounded-lg ${
                              selectedUHID === patient.uhid
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700'
                            }`}>
                              <CreditCard className="w-3.5 h-3.5" />
                              <span className="font-mono font-bold text-xs tracking-wide">
                                {patient.uhid}
                              </span>
                            </div>
                            {selectedUHID === patient.uhid && (
                              <span className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                <Star className="w-3 h-3 fill-current" />
                                <span>Selected</span>
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Quick Info */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center space-x-1.5 text-gray-600">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate">{patient.phoneNumber}</span>
                          </div>
                          <div className="flex items-center space-x-1.5 text-gray-600">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            <span>{patient.age} yrs • {patient.gender}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expand Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(patient.uhid);
                      }}
                      className={`ml-2 p-2 rounded-lg transition-all ${
                        expandedPatient === patient.uhid
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                      }`}
                    >
                      {expandedPatient === patient.uhid ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Expanded Details */}
                  {expandedPatient === patient.uhid && (
                    <div className="mt-4 pt-4 border-t-2 border-dashed border-gray-200 animate-fadeIn">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Location Info */}
                        <div className="bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-gray-200">
                          <div className="flex items-start space-x-2">
                            <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Location</p>
                              <p className="text-sm font-medium text-gray-800">{patient.city}</p>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">{patient.fullAddress}</p>
                            </div>
                          </div>
                        </div>

                        {/* Registration Info */}
                        {patient.registrationDate && (
                          <div className="bg-gradient-to-br from-gray-50 to-white p-3 rounded-lg border border-gray-200">
                            <div className="flex items-start space-x-2">
                              <Clock className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Registered On</p>
                                <p className="text-sm font-medium text-gray-800">
                                  {new Date(patient.registrationDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action hint */}
                      {selectedUHID !== patient.uhid && (
                        <div className="mt-3 text-center">
                          <p className="text-xs text-gray-500">
                            Click card to select this UHID
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
            )}
          </>
        )}

        {/* Chat History Tab Content */}
        {activeTab === 'chat' && (
          <div>
            {!selectedUHID ? (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No UHID Selected</h3>
                <p className="text-gray-500 text-sm">Please select a patient UHID to view chat history</p>
              </div>
            ) : loadingChatHistory ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : chatHistory.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No chats for this patient</h3>
                <p className="text-gray-500 text-sm">
                  No conversations found for UHID {selectedUHID}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {chatHistory.map((chat, index) => (
                  <div
                    key={index}
                    onClick={() => onSelectChat?.(chat)}
                    className="p-4 bg-gradient-to-r from-gray-50 to-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer group"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 mb-1 group-hover:text-purple-700 line-clamp-2">
                          {chat.message || chat.title || chat.chat_name || 'Untitled conversation'}
                        </p>
                        {chat.uhid && (
                          <div className="flex items-center space-x-2 mb-2">
                            <CreditCard className="w-3 h-3 text-gray-400" />
                            <span className="text-xs font-mono text-gray-600">UHID: {chat.uhid}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {chat.updated_at ? new Date(chat.updated_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : chat.timestamp ? new Date(chat.timestamp).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Recent'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Appointments Tab Content */}
        {activeTab === 'appointments' && (
          <div>
            {!selectedUHID ? (
              <div className="text-center py-12">
                <CalendarCheck className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No UHID Selected</h3>
                <p className="text-gray-500 text-sm">Please select a patient UHID to view appointments</p>
              </div>
            ) : loadingAppointments ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-12">
                <CalendarCheck className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No Appointments Found</h3>
                <p className="text-gray-500 text-sm">No appointments scheduled for UHID {selectedUHID}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {appointments.map((appointment, index) => {
                  const isHighlighted = highlightedAppointmentId === appointment.appointment_id;
                  return (
                  <div
                    key={appointment.appointment_id || index}
                    className={`p-4 bg-gradient-to-r rounded-xl hover:shadow-lg transition-all ${
                      isHighlighted
                        ? 'from-blue-100 to-blue-50 border-4 border-blue-500 shadow-xl'
                        : 'from-green-50 to-white border-2 border-green-200'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                        <CalendarCheck className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 mb-2">
                          {appointment.doctorName || 'Doctor Appointment'}
                        </p>
                        {appointment.appointment_id && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              const newHighlightedId = highlightedAppointmentId === appointment.appointment_id
                                ? null
                                : appointment.appointment_id;

                              setHighlightedAppointmentId(newHighlightedId);

                              // Auto-fill appointment ID but NO page opening (empty action)
                              if (onSelectAppointment) {
                                onSelectAppointmentWithoutModal(newHighlightedId || '', '');
                              }
                            }}
                            className={`mb-2 p-2 rounded border cursor-pointer transition-all ${
                              isHighlighted
                                ? 'bg-blue-200 border-blue-400 shadow-md'
                                : 'bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-xs text-blue-600 font-semibold">Appointment ID: </span>
                                <span className="text-xs font-mono text-blue-800 font-bold">{appointment.appointment_id}</span>
                              </div>
                              {isHighlighted && (
                                <div className="flex items-center gap-1 bg-green-500 text-white px-2 py-1 rounded-full">
                                  <CheckCircle className="w-3 h-3" />
                                  <span className="text-xs font-bold">Selected</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {appointment.speciality && (
                          <div className="flex items-center space-x-2 mb-2">
                            <User className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-600">{appointment.speciality}</span>
                          </div>
                        )}
                        {appointment.hospital && (
                          <div className="flex items-center space-x-2 mb-2">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-600">{appointment.hospital}</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              {appointment.appointmentDate ? new Date(appointment.appointmentDate).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              }) : 'Date TBD'}
                            </span>
                          </div>
                          {appointment.timeSlot && (
                            <div className="flex items-center space-x-2">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500">{appointment.timeSlot}</span>
                            </div>
                          )}
                        </div>
                        {appointment.status && (
                          <div className="mt-2 flex items-center justify-between">
                            <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                              appointment.status === 'confirmed'
                                ? 'bg-green-100 text-green-700'
                                : appointment.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : appointment.status === 'cancelled'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-700'
                            }`}>
                              {appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                            </span>

                            {/* Action buttons - only show if not cancelled */}
                            {appointment.status !== 'cancelled' && (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectAppointment?.(appointment.appointment_id, 'reschedule');
                                  }}
                                  className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors"
                                  title="Reschedule this appointment"
                                >
                                  Reschedule
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSelectAppointment?.(appointment.appointment_id, 'cancel');
                                  }}
                                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded transition-colors"
                                  title="Cancel this appointment"
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Info - Only show in UHID tab */}
      {selectedUHID && activeTab === 'uhid' && (
        <div className="flex-shrink-0 bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 border-t-2 border-green-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-green-800">Active Selection</p>
              <p className="text-xs text-green-700">
                UHID <span className="font-mono font-bold">{selectedUHID}</span> will be used for appointments
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #60a5fa, #3b82f6);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #3b82f6, #2563eb);
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
      </div>
    </div>
  );
};

export default UHIDSelector;
